import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useResolveDID } from '../api/hooks';

const EXAMPLE_DIDS = [
  { label: 'did:adi', value: 'did:adi:Bkmn1qJjsJynMS9MonedxvTA8363thywJGNnUnujEc6M' },
  { labelKey: 'resolver.examples.key', value: 'did:key:z6MkhaXgBZDvotDkL5257faiztiGiC2QtKLGpbnnEGta2doK' },
  { labelKey: 'resolver.examples.web', value: 'did:web:example.com' },
  { labelKey: 'resolver.examples.ethr', value: 'did:ethr:0xE6Fe788d8ca214A080b0f6aC7F48480b2AEfa9a6' },
];

export default function DIDResolver() {
  const [input, setInput] = useState('');
  const { data: result, loading, error, resolve } = useResolveDID();
  const [copied, setCopied] = useState(false);
  const { t } = useTranslation(['dids', 'common']);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const did = searchParams.get('did');
    if (did) {
      setInput(did);
      resolve(did);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleResolve = () => {
    if (input.trim()) resolve(input.trim());
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleResolve();
  };

  const copyJSON = () => {
    if (result) {
      navigator.clipboard.writeText(JSON.stringify(result, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const doc = result?.didDocument as Record<string, unknown> | undefined;
  const metadata = result?.didDocumentMetadata as Record<string, unknown> | undefined;
  const method = input.split(':')[1] || '';

  const methodColors: Record<string, string> = {
    adi: 'bg-blue-50 text-blue-700 border-blue-200',
    key: 'bg-purple-50 text-purple-700 border-purple-200',
    web: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    ethr: 'bg-amber-50 text-amber-700 border-amber-200',
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-text-main">{t('resolver.title')}</h1>
        <p className="mt-1 text-sm text-text-muted">{t('resolver.subtitle')}</p>
      </div>

      <div className="rounded-xl border border-border-subtle bg-white p-5">
        <div className="flex gap-3">
          <div className="relative flex-1">
            <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-text-faint" style={{ fontSize: 20 }}>
              travel_explore
            </span>
            <input
              type="text"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('resolver.placeholder')}
              className="w-full rounded-lg border border-border-subtle py-2.5 pl-10 pr-4 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <button onClick={handleResolve} disabled={loading || !input.trim()} className="btn-primary px-6">
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                {t('resolver.resolving')}
              </span>
            ) : t('resolver.resolve')}
          </button>
        </div>

        <div className="mt-3 flex items-center gap-2">
          <span className="text-xs text-text-faint">{t('resolver.tryLabel')}</span>
          {EXAMPLE_DIDS.map(ex => (
            <button
              key={ex.value}
              onClick={() => { setInput(ex.value); resolve(ex.value); }}
              className="rounded-full border border-border-subtle px-2.5 py-0.5 text-[10px] font-bold text-text-faint hover:border-primary hover:text-primary"
            >
              {'label' in ex ? ex.label : t((ex as { labelKey: string }).labelKey)}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4">
          <div className="flex items-center gap-2 text-red-700">
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>error</span>
            <span className="text-sm font-bold">{t('common:errors.resolutionFailed')}</span>
          </div>
          <p className="mt-1 text-xs text-red-600">{error}</p>
        </div>
      )}

      {doc && (
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-4">
            <div className="rounded-xl border border-border-subtle bg-white p-4">
              <p className="text-xs font-bold text-text-faint">{t('common:labels.method')}</p>
              <span className={`mt-1 inline-flex rounded-full border px-2.5 py-1 text-xs font-bold ${methodColors[method] || 'bg-slate-50 text-slate-700 border-slate-200'}`}>
                did:{method}
              </span>
            </div>
            <div className="rounded-xl border border-border-subtle bg-white p-4">
              <p className="text-xs font-bold text-text-faint">{t('common:labels.verificationMethods')}</p>
              <p className="mt-1 text-2xl font-bold text-text-main">
                {(doc.verificationMethod as unknown[])?.length ?? 0}
              </p>
            </div>
            <div className="rounded-xl border border-border-subtle bg-white p-4">
              <p className="text-xs font-bold text-text-faint">{t('common:labels.services')}</p>
              <p className="mt-1 text-2xl font-bold text-text-main">
                {(doc.service as unknown[])?.length ?? 0}
              </p>
            </div>
            <div className="rounded-xl border border-border-subtle bg-white p-4">
              <p className="text-xs font-bold text-text-faint">{t('common:labels.status')}</p>
              <span className={`mt-1 inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${
                doc.deactivated ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'
              }`}>
                <span className={`size-1.5 rounded-full ${doc.deactivated ? 'bg-red-500' : 'bg-emerald-500'}`} />
                {doc.deactivated ? t('common:status.deactivated') : t('common:status.active')}
              </span>
            </div>
          </div>

          {metadata && Object.keys(metadata).length > 0 && (
            <div className="rounded-xl border border-border-subtle bg-white p-4">
              <p className="mb-2 text-xs font-bold text-text-faint">{t('common:labels.resolutionMetadata')}</p>
              <div className="flex flex-wrap gap-3">
                {Object.entries(metadata).map(([k, v]) => (
                  <span key={k} className="rounded-lg bg-slate-50 px-2.5 py-1 text-xs">
                    <span className="font-bold text-text-faint">{k}:</span>{' '}
                    <span className="text-text-main">{String(v)}</span>
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-xl border border-border-subtle bg-white p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-bold text-text-faint">{t('common:labels.didDocumentShort')}</p>
              <button onClick={copyJSON} className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-text-faint hover:bg-slate-50 hover:text-primary">
                <span className="material-symbols-outlined" style={{ fontSize: 14 }}>{copied ? 'check' : 'content_copy'}</span>
                {copied ? t('resolver.copied') : t('resolver.copy')}
              </button>
            </div>
            <pre className="max-h-[500px] overflow-auto rounded-lg bg-slate-50 p-4 font-mono text-xs text-text-main">
              {JSON.stringify(doc, null, 2)}
            </pre>
          </div>
        </div>
      )}

      {!doc && !error && !loading && (
        <div className="flex flex-col items-center justify-center rounded-xl border-2 border-dashed border-border-subtle py-20">
          <span className="material-symbols-outlined mb-4 text-text-faint" style={{ fontSize: 48 }}>travel_explore</span>
          <p className="text-sm font-bold text-text-main">{t('resolver.empty.title')}</p>
          <p className="mt-1 text-xs text-text-faint">{t('resolver.empty.subtitle')}</p>
        </div>
      )}
    </div>
  );
}
